import { Injectable } from '@angular/core';
import { ElementAttribute, ElementTagName } from '@models/editor';
import { ElementPositionHandleHelperService, PositionAxis } from './element-position-handle-helper.service';
import { TriangleDrawHelperService } from './triangle-draw-helper.service';
import { CanvasInputMode } from './canvas-input-mode.service';

export const libraryComponentBgWidth = 1200;
export const libraryComponentBgHeight = 900;

@Injectable({ providedIn: 'root' })
export class LibraryComponentHelperService {
    readonly POSITION_X = PositionAxis.positionX;
    readonly POSITION_Y = PositionAxis.positionY;

    readonly LIBRARY_COMPONENT_BG_WIDTH = libraryComponentBgWidth;
    readonly LIBRARY_COMPONENT_BG_HEIGHT = libraryComponentBgHeight;

    constructor(
        private elementPositionHandleService: ElementPositionHandleHelperService,
        private triangleService: TriangleDrawHelperService
    ) {}

    saveElementPosition(element: HTMLElement, prevElement: HTMLElement) {
        if (element.tagName === ElementTagName.rect) {
            const width = Number(element.getAttribute(ElementAttribute.width));
            const height = Number(element.getAttribute(ElementAttribute.height));

            element.setAttribute(ElementAttribute.x, String((this.LIBRARY_COMPONENT_BG_WIDTH - width) / 2));
            element.setAttribute(ElementAttribute.y, String((this.LIBRARY_COMPONENT_BG_HEIGHT - height) / 2));
        } else if (element.tagName === ElementTagName.ellipse) {
            element.setAttribute(ElementAttribute.cx, String(this.LIBRARY_COMPONENT_BG_WIDTH / 2));
            element.setAttribute(ElementAttribute.cy, String(this.LIBRARY_COMPONENT_BG_HEIGHT / 2));
        } else if (element.tagName === ElementTagName.g) {
            const { dx, dy } = this.getGroupPosition(prevElement);
            this.setPathPosition(prevElement, element, dx, dy);
        } else if (element.tagName === ElementTagName.line) {
            const bbox = (prevElement as any).getBBox();

            const targetCx = this.LIBRARY_COMPONENT_BG_WIDTH / 2;
            const targetCy = this.LIBRARY_COMPONENT_BG_HEIGHT / 2;
            const currentCx = bbox.width / 2;
            const currentCy = bbox.height / 2;
            const dx = targetCx - currentCx;
            const dy = targetCy - currentCy;

            if (dx) this.elementPositionHandleService.handlePositionChange(this.POSITION_X, dx, [element], true, false);
            if (dy) this.elementPositionHandleService.handlePositionChange(this.POSITION_Y, dy, [element], true, false);
        } else if (element.tagName === ElementTagName.path) {
            const bbox = (prevElement as any).getBBox();
            const { dx, dy } = this.getPosition(prevElement);

            if (element.getAttribute(ElementAttribute.type) === CanvasInputMode.TRIANGLE) {
                this.setTrianglePosition(element, bbox, dx, dy);
            } else {
                this.setPathPosition(prevElement, element, dx, dy);
            }
        } else if (element.tagName === ElementTagName.image) {
            const { dx, dy } = this.getPosition(prevElement);

            element.setAttribute(ElementAttribute.x, dx.toString());
            element.setAttribute(ElementAttribute.y, dy.toString());
        }
    }

    getPosition(element: Element) {
        const bbox = (element as any).getBBox();

        const targetCx = this.LIBRARY_COMPONENT_BG_WIDTH / 2;
        const targetCy = this.LIBRARY_COMPONENT_BG_HEIGHT / 2;
        const currentCx = bbox.x + bbox.width / 2;
        const currentCy = bbox.y + bbox.height / 2;

        const dx = targetCx - currentCx;
        const dy = targetCy - currentCy;

        return { dx, dy };
    }

    getGroupPosition(element: Element) {
        const bbox = (element as any).getBBox();
        const transformList = (element as SVGGraphicsElement).transform.baseVal;

        let matrix = new DOMMatrix();
        for (let i = 0; i < transformList.numberOfItems; i++) {
            matrix = matrix.multiply(transformList.getItem(i).matrix);
        }

        const center = new DOMPoint(bbox.x + bbox.width / 2, bbox.y + bbox.height / 2).matrixTransform(matrix);

        return {
            dx: this.LIBRARY_COMPONENT_BG_WIDTH / 2 - center.x,
            dy: this.LIBRARY_COMPONENT_BG_HEIGHT / 2 - center.y,
        };
    }

    setPathPosition(prevElement: Element, element: Element, dx: number, dy: number) {
        const svg = (prevElement as any).ownerSVGElement;
        if (!svg) return;

        const t = svg.createSVGTransform();
        t.setTranslate(dx, dy);

        const list = (element as any).transform.baseVal;
        if (list.numberOfItems > 0) {
            list.insertItemBefore(t, 0);
        } else {
            list.appendItem(t);
        }
    }

    setTrianglePosition(element: Element, bbox, dx: number, dy: number) {
        this.triangleService.updateTrianglePathAndRotation(
            element as any,
            bbox.x + dx,
            bbox.y + dy,
            bbox.width,
            bbox.height
        );
    }

    getBBoxPosition(element) {
        const bbox = (element as any).getBBox();
        return bbox;
    }
}
